import React from 'react';
import { GovMessage } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { useEPeticeEditContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	DataTable,
	FormFileUpload,
	FormSelect,
	FormTextarea,
	FormWidthElementLayout,
	Icon,
	LayoutGap,
	optionsToChildren,
	PetitionAddresseeAutocomplete,
} from '@gov-nx/ui/web';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';
import { WizardStep2ContactDetails } from './WizardStep2ContactDetails';
import { ElementIds } from './utils';

export const WizardStep2 = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const context = useEPeticeEditContext();
	const {
		applicant,
		onRemoveMember,
		formStep2,
		code,
		isProcessing,
		isPetitionMemberAddressLookupModalOpen,
		zakladatelTyp,
	} = context;
	const tsn = getLocalizeCurried(code);

	return (
		<LayoutGap gap="xl">
			<LayoutGap gap="m">
				<h2
					className="text-headline-m!"
					id={ElementIds.popis}>
					{tsn('formular.nadpis.popis-epetice')}
				</h2>

				<LayoutGap gap="xl">
					<FormTextarea
						label={{ children: tsn('formular.pole.perex') }}
						field={{
							rows: 4,
							name: 'perex',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.perex'),
						}}
					/>
					<FormTextarea
						label={{ children: tsn('formular.pole.text') }}
						field={{
							rows: 4,
							name: 'text',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.text'),
						}}
					/>
				</LayoutGap>
			</LayoutGap>

			<LayoutGap gap="m">
				<h2
					className="text-headline-m!"
					id={ElementIds.priloha}>
					{tsn('formular.nadpis.priloha')}
				</h2>

				<FormFileUpload {...formStep2.fields.attachments} />
			</LayoutGap>

			{zakladatelTyp === 'PO' && (
				<div>
					<FormWidthElementLayout type="l">
						<FormSelect
							toValue={(ico: string) => parseInt(ico, 10)}
							field={{
								isDisabled:
									isProcessing || context.selectIcNumberOptions.length <= 1,
								name: 'zakladatelPoIco',
								children: optionsToChildren(context.selectIcNumberOptions),
							}}
							label={{ children: tsn('formular.pole.ico') }}
						/>
					</FormWidthElementLayout>

					{context.selectedIcNumber && (
						<LayoutGap
							gap="m"
							className="mt-4">
							<MarkdownRender>
								{tsn('formular.popis.zakladatel-po')}
							</MarkdownRender>
							<DataTable responsive>
								<tbody>
									<tr>
										<td>{tsn('formular.zakladatel.adresa')}</td>
										<td>
											<strong>
												{
													applicant.businessPerson?.seznam?.find(
														(business) =>
															business.ico === context.selectedIcNumber
													)?.sidloSpolecnosti?.adresaText
												}
											</strong>
										</td>
									</tr>
									<tr>
										<td>{tsn('formular.zakladatel.datova-schranka')}</td>
										<td>
											<strong>
												{
													applicant.businessPerson?.seznam?.find(
														(business) =>
															business.ico === context.selectedIcNumber
													)?.datovaSchrankaId
												}
											</strong>
										</td>
									</tr>
								</tbody>
							</DataTable>
						</LayoutGap>
					)}
				</div>
			)}

			{formStep2 ? <WizardStep2ContactDetails /> : null}

			<LayoutGap gap="m">
				<h2
					className="text-headline-m!"
					id={ElementIds.peticniVybor}>
					{tsn('formular.nadpis.peticni-vybor')}
				</h2>
				<p>{tsn('formular.popisek.peticni-vybor')}</p>
				{zakladatelTyp === 'FO' ? (
					<div>
						{!formStep2?.formDefinition.formMethods.watch(
							'clenovePeticnihoVyboru'
						)?.length ? (
							''
						) : (
							<ol className="mb-4">
								{formStep2?.formDefinition.formMethods
									.watch('clenovePeticnihoVyboru')
									?.map((member, index) => (
										<li
											key={[
												member.id,
												member.jmeno,
												member.prijmeni,
												member.adresa?.adresaText,
											].join('-')}>
											{member.jmeno} {member.prijmeni},{' '}
											{member.adresa?.adresaText}
											<Button
												onClick={() => onRemoveMember(index)}
												size="xs"
												color="primary"
												type="base"
												isDisabled={isProcessing}
												aria-label={tsn('akce.wcag.odebrat-clena')}
												iconStart={
													<Icon
														name="x-lg"
														type="components"
													/>
												}
											/>
										</li>
									))}
							</ol>
						)}
						<Button
							onClick={isPetitionMemberAddressLookupModalOpen.setTrue}
							isLoading={isProcessing}
							aria-label={tsn('akce.wcag.pridat-clena')}
							isDisabled={isProcessing}
							color="primary"
							type="outlined"
							size="s"
							iconStart={
								<Icon
									name="plus-lg"
									type="templates"
								/>
							}>
							{tsn('formular.tlacitko.pridat-clena')}
						</Button>
					</div>
				) : (
					<GovMessage
						color="warning"
						type="subtle"
						icon={
							<Icon
								type="components"
								name="info-circle-fill"
							/>
						}>
						<MarkdownRender>
							{tsn('formular.zprava.peticni-vybor-varovani-po')}
						</MarkdownRender>
					</GovMessage>
				)}
			</LayoutGap>

			<LayoutGap gap="m">
				<h2
					className="text-headline-m!"
					id={ElementIds.adresat}>
					{tsn('formular.nadpis.adresat')}
				</h2>
				<MarkdownRender>{tsn('formular.poznamka.adresat')}</MarkdownRender>
				<FormWidthElementLayout type="l">
					<PetitionAddresseeAutocomplete
						field={{
							name: 'adresatOvm',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.adresat'),
						}}
						label={{
							children: tsn('formular.pole.adresat'),
						}}
					/>
				</FormWidthElementLayout>
			</LayoutGap>

			<ButtonGroupLayout>
				<Button
					isLoading={isProcessing}
					aria-label={tsn('akce.wcag.pokracovat')}
					isDisabled={
						isProcessing ||
						formStep2.fields.attachments.fileUploadHook.hasNotFinishedUploads
					}
					nativeType="submit"
					color="primary"
					size="l"
					type="solid">
					{tsn('formular.tlacitko.pokracovat')}
				</Button>
			</ButtonGroupLayout>
		</LayoutGap>
	);
};
